import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { LuStore, LuWallet, LuCalendarDays, LuTrendingUp, LuReceipt, LuMapPin, LuPhone, LuLayoutDashboard, LuChartLine, LuStar, LuBanknote, LuSmartphone, LuNotebookPen } from 'react-icons/lu';
import API from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/layout/AppLayout';
import {  Page, PageHeader, Card, StatCard, Grid, Badge, Tabs, EmptyState, Loader, Button, ProgressRow, Valeur } from '../../components/ui';
import { C, ROLES, fmtK, fmtF } from '../../theme';

const ACCENT = ROLES.proprietaire.accent;

const TABS = [
  { id: 'overview',       label: 'Vue d\'ensemble', icon: LuLayoutDashboard },
  { id: 'etablissements', label: 'Établissements',  icon: LuStore },
  { id: 'finances',       label: 'Finances',        icon: LuChartLine },
  { id: 'abonnement',     label: 'Abonnement',      icon: LuStar },
];

const MODES = {
  especes:      { label: 'Espèces',      icon: LuBanknote,    color: C.success },
  mobile_money: { label: 'Mobile Money', icon: LuSmartphone,  color: C.info },
  credit:       { label: 'Crédit',       icon: LuNotebookPen, color: ACCENT },
};

const montant = v => parseFloat(v.montant_total || 0);
const somme = ventes => ventes.reduce((t, v) => t + montant(v), 0);

export default function DashboardProprietaire() {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const [onglet,         setOnglet]         = useState('overview');
  const [chargement,     setChargement]     = useState(true);
  const [etablissements, setEtablissements] = useState([]);
  const [ventes,         setVentes]         = useState([]);
  const [abonnement,     setAbonnement]     = useState(null);

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    try {
      const [etabRes, ventesRes] = await Promise.all([
        API.get('/etablissements'),
        API.get('/ventes'),
      ]);
      setEtablissements(etabRes.data.etablissements || []);
      setVentes(ventesRes.data.ventes || []);
      try {
        const abRes = await API.get('/abonnements/etat');
        // Établissement dont l'échéance est la plus proche
        const liste = (abRes.data.etablissements || []).filter(e => e.date_fin)
          .sort((a, b) => new Date(a.date_fin) - new Date(b.date_fin));
        setAbonnement(liste[0] ? { ...liste[0], plan: liste[0].plan_nom, statut: liste[0].etat === 'actif' || liste[0].etat === 'expire_bientot' ? 'actif' : liste[0].etat.replace('_', ' ') } : null);
      } catch {}
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  // ── Calculs ──────────────────────────────────────────────────
  const ventesValidees = ventes.filter(v => v.statut === 'validee');
  const today = new Date().toDateString();
  const ventesJour = ventesValidees.filter(v => new Date(v.created_at).toDateString() === today);
  const caJour  = somme(ventesJour);
  const now = new Date();
  const caMois  = somme(ventesValidees.filter(v => {
    const d = new Date(v.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }));
  const caTotal = somme(ventesValidees);

  const caParJour = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const label = d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
    const ca = somme(ventesValidees.filter(v => new Date(v.created_at).toDateString() === d.toDateString()));
    return { label, ca: Math.round(ca) };
  });

  const statsEtab = e => {
    const vs = ventesValidees.filter(v => v.etablissement_id === e.id);
    const ca = somme(vs);
    return { ca, nb: vs.length, moy: vs.length ? ca / vs.length : 0 };
  };

  const caParEtab = etablissements.map(e => ({
    nom: e.nom.length > 14 ? e.nom.slice(0, 14) + '…' : e.nom,
    ca: Math.round(statsEtab(e).ca),
  }));

  const joursRestants = abonnement
    ? Math.max(0, Math.ceil((new Date(abonnement.date_fin) - new Date()) / 86400000))
    : null;
  const toneAbo = joursRestants === null ? 'neutral' : joursRestants <= 3 ? 'danger' : joursRestants <= 7 ? 'warn' : 'success';

  if (chargement) return <Loader />;

  const heure = new Date().getHours();
  const salut = heure < 18 ? 'Bonjour' : 'Bonsoir';

  return (
    <AppLayout role="proprietaire">
      <Page>
        <PageHeader
          title={`${salut}, ${utilisateur?.prenom || ''}`}
          subtitle={new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        />

        {/* L'alerte d'échéance est affichée par le bandeau commun (AbonnementBandeau). */}
        <Tabs tabs={TABS} value={onglet} onChange={setOnglet} />

        {/* ══ VUE D'ENSEMBLE ══ */}
        {onglet === 'overview' && (
          <>
            <Grid min={210} style={{ marginBottom: 20 }}>
              <StatCard icon={LuWallet}       label="Revenus du jour"  value={`${fmtF(caJour)}`}  hint={`${ventesJour.length} vente${ventesJour.length > 1 ? 's' : ''}`} color={C.success} />
              <StatCard icon={LuCalendarDays} label="Revenus du mois"      value={`${fmtF(caMois)}`}  color={C.info} />
              <StatCard icon={LuTrendingUp}   label="Revenus totaux"        value={`${fmtF(caTotal)}`} color={C.purple} />
              <StatCard icon={LuStore}        label="Établissements"  value={etablissements.length} color={ACCENT} />
            </Grid>

            <Grid min={340} style={{ marginBottom: 20 }}>
              <Card title="Revenus — 7 derniers jours">
                <CourbeCA data={caParJour} height={220} />
              </Card>

              {etablissements.length > 1 ? (
                <Card title="Revenus par établissement">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={caParEtab} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={C.border} />
                      <XAxis dataKey="nom" tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} width={48} />
                      <Tooltip formatter={v => [`${fmtF(v)}`, 'Revenus']} contentStyle={tooltipStyle} cursor={{ fill: C.separator }} />
                      <Bar dataKey="ca" fill={ACCENT} radius={[6, 6, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              ) : (
                <Card title="Ventes récentes" action={<Button variant="secondary" onClick={() => setOnglet('finances')} style={{ minHeight: 32, fontSize: 13 }}>Tout voir</Button>}>
                  {ventes.length === 0
                    ? <EmptyState icon={LuReceipt} title="Aucune vente" text="Les ventes apparaîtront ici." />
                    : ventes.slice(0, 5).map(v => <VenteRow key={v.id} vente={v} />)}
                </Card>
              )}
            </Grid>

            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 12px', color: C.text }}>Mes établissements</h2>
            {etablissements.length === 0
              ? <Card><EmptyState icon={LuStore} title="Aucun établissement" /></Card>
              : (
                <Grid min={280}>
                  {etablissements.map(e => {
                    const st = statsEtab(e);
                    return (
                      <Card key={e.id}>
                        <EtabEntete etab={e} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
                          <MiniStat label="Revenus" value={`${fmtF(st.ca)}`} />
                          <MiniStat label="Ventes" value={st.nb} />
                        </div>
                      </Card>
                    );
                  })}
                </Grid>
              )}
          </>
        )}

        {/* ══ ÉTABLISSEMENTS ══ */}
        {onglet === 'etablissements' && (
          etablissements.length === 0
            ? <Card><EmptyState icon={LuStore} title="Aucun établissement" text="Contactez l'administrateur pour en créer un." /></Card>
            : (
              <div style={{ display: 'grid', gap: 12 }}>
                {etablissements.map(e => {
                  const st = statsEtab(e);
                  return (
                    <Card key={e.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 260px' }}><EtabEntete etab={e} grand /></div>
                        {e.telephone && (
                          <a href={`tel:${e.telephone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textSub, fontSize: 13, textDecoration: 'none' }}>
                            <LuPhone aria-hidden /> {e.telephone}
                          </a>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 16 }}>
                        <MiniStat label="Revenus totaux"      value={`${fmtF(st.ca)}`} />
                        <MiniStat label="Ventes"        value={st.nb} />
                        <MiniStat label="Panier moyen"  value={st.nb ? `${fmtF(st.moy)}` : '—'} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            )
        )}

        {/* ══ FINANCES ══ */}
        {onglet === 'finances' && (
          <>
            <Grid min={210} style={{ marginBottom: 20 }}>
              <StatCard icon={LuWallet}       label="Revenus du jour" value={`${fmtF(caJour)}`}   color={C.success} />
              <StatCard icon={LuCalendarDays} label="Revenus du mois"     value={`${fmtF(caMois)}`}  color={C.info} />
              <StatCard icon={LuTrendingUp}   label="Revenus totaux"       value={`${fmtF(caTotal)}`} color={C.purple} />
              <StatCard icon={LuReceipt}      label="Ventes validées" value={ventesValidees.length} color={ACCENT} />
            </Grid>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
              <Card title="Évolution des revenus — 7 jours" style={{ flex: '2 1 420px', minWidth: 0 }}>
                <CourbeCA data={caParJour} height={240} />
              </Card>
              <Card title="Répartition des paiements" style={{ flex: '1 1 280px' }}>
                {Object.entries(MODES).map(([mode, cfg]) => {
                  const total = somme(ventesValidees.filter(v => v.mode_paiement === mode));
                  const Icon = cfg.icon;
                  return (
                    <ProgressRow
                      key={mode}
                      label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon aria-hidden style={{ color: cfg.color }} />{cfg.label}</span>}
                      value={`${fmtF(total)}`}
                      pct={caTotal > 0 ? (total / caTotal) * 100 : 0}
                      color={cfg.color}
                    />
                  );
                })}
              </Card>
            </div>

            <Card title="Ventes récentes" style={{ padding: 0, overflow: 'hidden' }}>
              {ventes.length === 0
                ? <EmptyState icon={LuReceipt} title="Aucune vente" />
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 560 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F8FAFC' }}>
                          {['Facture', 'Date', 'Paiement', 'Montant', 'Statut'].map((h, i) => (
                            <th key={h} style={{ ...thStyle, textAlign: i === 3 ? 'right' : 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ventes.slice(0, 20).map(v => {
                          const mode = MODES[v.mode_paiement];
                          const d = new Date(v.created_at);
                          return (
                            <tr key={v.id} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{v.numero_facture || '#' + v.id}</td>
                              <td style={{ ...tdStyle, color: C.textSub }}>
                                {d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                {' · '}
                                {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td style={{ ...tdStyle, color: C.textSub }}>{mode?.label || v.mode_paiement}</td>
                              <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>{fmtF(v.montant_total)}</td>
                              <td style={tdStyle}>
                                <Badge tone={v.statut === 'validee' ? 'success' : 'danger'}>
                                  {v.statut === 'validee' ? 'Validée' : 'Annulée'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </Card>
          </>
        )}

        {/* ══ ABONNEMENT ══ */}
        {onglet === 'abonnement' && (
          <div style={{ maxWidth: 560 }}>
            {abonnement ? (
              <Card style={{ borderTop: `4px solid ${toneAbo === 'success' ? ACCENT : toneAbo === 'danger' ? C.danger : C.warn}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, display: 'grid', placeItems: 'center', fontSize: 22, color: ACCENT, backgroundColor: ROLES.proprietaire.accentLight }}>
                    <LuStar aria-hidden />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>Plan actuel</div>
                    <div style={{ fontWeight: 800, fontSize: 22, color: C.text, textTransform: 'capitalize' }}>{abonnement.plan || 'Standard'}</div>
                  </div>
                  <Badge tone={abonnement.statut === 'actif' ? 'success' : 'danger'}>{abonnement.statut}</Badge>
                </div>
                {[
                  { label: 'Établissement', value: abonnement.etablissement_nom },
                  { label: 'Expiration', value: new Date(abonnement.date_fin).toLocaleDateString('fr-FR') },
                  {
                    label: 'Temps restant',
                    value: <Badge tone={toneAbo}>{joursRestants <= 0 ? 'Expiré' : `${joursRestants} jour${joursRestants > 1 ? 's' : ''}`}</Badge>,
                  },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${C.border}` }}>
                    <span style={{ color: C.textSub }}>{row.label}</span>
                    <span style={{ fontWeight: 600, color: C.text }}>{row.value}</span>
                  </div>
                ))}
                <Button style={{ width: '100%', marginTop: 16, minHeight: 46 }} onClick={() => navigate('/proprietaire/abonnement')}>
                  {joursRestants <= 30 ? "Renouveler l'abonnement" : 'Gérer mes abonnements'}
                </Button>
              </Card>
            ) : (
              <Card>
                <EmptyState icon={LuStar} title="Aucun abonnement actif" text="Souscrivez pour continuer à utiliser StockPro." action={<Button onClick={() => navigate('/proprietaire/abonnement')}>Voir les abonnements</Button>} />
              </Card>
            )}
          </div>
        )}
      </Page>
    </AppLayout>
  );
}

// ── Sous-composants ─────────────────────────────────────────────
function CourbeCA({ data, height }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="caFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity={0.25} />
            <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={C.border} />
        <XAxis dataKey="label" tick={{ fontSize: 12, fill: C.textMuted }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: C.textMuted }} axisLine={false} tickLine={false} width={48} />
        <Tooltip formatter={v => [`${fmtF(v)}`, 'Revenus']} contentStyle={tooltipStyle} />
        <Area type="monotone" dataKey="ca" stroke={ACCENT} strokeWidth={2.5} fill="url(#caFill)" dot={{ r: 3, fill: ACCENT, strokeWidth: 0 }} activeDot={{ r: 5 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function EtabEntete({ etab, grand }) {
  const taille = grand ? 52 : 44;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <div style={{ width: taille, height: taille, borderRadius: 12, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: grand ? 24 : 20, color: ACCENT, backgroundColor: ROLES.proprietaire.accentLight }}>
        <LuStore aria-hidden />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: grand ? 16 : 15, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{etab.nom}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: C.textMuted }}>
          <LuMapPin aria-hidden style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {[etab.adresse, etab.ville].filter(Boolean).join(' · ') || 'Aucune adresse'}
          </span>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={{ backgroundColor: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: 16, color: C.text, fontVariantNumeric: 'tabular-nums' }}><Valeur value={value} /></div>
    </div>
  );
}

function VenteRow({ vente }) {
  const annulee = vente.statut !== 'validee';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderTop: `1px solid ${C.separator}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'ui-monospace, monospace', color: C.text }}>{vente.numero_facture || '#' + vente.id}</div>
        <div style={{ fontSize: 12, color: C.textMuted }}>
          {new Date(vente.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: annulee ? C.textMuted : C.text, textDecoration: annulee ? 'line-through' : 'none' }}>
        {fmtF(vente.montant_total)}
      </div>
    </div>
  );
}

const tooltipStyle = { borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, boxShadow: '0 4px 12px rgba(15,23,42,0.08)' };
const thStyle = { padding: '10px 16px', color: C.textMuted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '12px 16px', color: C.text };
